/**
 * The small pieces every /downhole screen draws the same way: the status
 * badge, the category pill and the wear meter. Local to the vertical — the
 * Quotes / Equipment badge idiom (saturated signal fill, dark ink), spelled
 * once here rather than per screen.
 */
import React from 'react';
import { DH_CATEGORY_LABEL, dhLabel, dhSkin, type DhStatusSkin } from '../../lib/downhole-labels';
import { formatWearPercent } from '../../lib/downhole-units';
import './downhole.css';

export function DhBadge({ map, value }: { map: Record<string, DhStatusSkin>; value: string | null | undefined }) {
  const skin = dhSkin(map, value);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/** A category is a kind, not a state, so it takes the neutral outlined pill. */
export function DhCategoryPill({ category }: { category: string | null | undefined }) {
  return (
    <span className="inline-flex whitespace-nowrap rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text">
      {dhLabel(DH_CATEGORY_LABEL, category)}
    </span>
  );
}

/**
 * WEAR USED, as the API returned it — never "remaining", never computed here.
 *
 * Null (the component has no limit — 14 of the Permian demo's 40) renders `—`
 * and an EMPTY track, not a 0% bar: an untracked tool has no wear figure, and
 * a zero would read as "new". The colour bands match the API's default alert
 * threshold (85) and are colour only; the number is the API's.
 */
export function DhWearMeter({ percent, label }: { percent: number | null | undefined; label?: string }) {
  const known = typeof percent === 'number' && Number.isFinite(percent);
  const fill = !known ? '' : percent >= 85 ? 'bg-fi-signal-red' : percent >= 60 ? 'bg-fi-signal-amber' : 'bg-fi-signal-green';
  return (
    <span className="inline-flex items-center gap-2" title={label}>
      <span
        className="h-1.5 w-16 overflow-hidden rounded-full bg-fieldiq-border"
        role="meter"
        aria-label={label ?? 'Wear used'}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={known ? percent : undefined}
        aria-valuetext={known ? `${formatWearPercent(percent)} used` : 'No wear limit set'}
      >
        {known && <span className={`block h-full rounded-full ${fill}`} style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }} />}
      </span>
      <span className="font-mono text-xs tabular-nums text-fieldiq-text">{formatWearPercent(percent)}</span>
    </span>
  );
}

/**
 * The run's state, from the API's `run_state`. A closed run carries no pill —
 * it is the ordinary case — and an unknown value claims nothing.
 */
export function RunStatePill({ state }: { state: string | null | undefined }) {
  if (state !== 'open' && state !== 'not_run') return null;
  return (
    <span
      className={`rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink ${
        state === 'open' ? 'bg-fi-signal-blue' : 'bg-fi-signal-slate'
      }`}
    >
      {state === 'open' ? 'In the hole' : 'Not run'}
    </span>
  );
}

/** One labelled figure in a detail grid. `value` arrives already formatted. */
export function DhSpec({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</dt>
      <dd className={`mt-1 truncate text-sm font-semibold text-white ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</dd>
    </div>
  );
}

/** The register panel: one amber left edge, eyebrow + h2 header. */
export function DhPanel({
  eyebrow,
  title,
  testId,
  actions,
  children,
  footer,
}: {
  eyebrow: string;
  title: string;
  testId?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
          <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">{title}</h2>
        </div>
        {actions}
      </header>
      {children}
      {footer && <footer className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">{footer}</footer>}
    </section>
  );
}

/** The loaded-and-genuinely-empty state, worded as a fact about the data. */
export function DhEmpty({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-10 text-center text-sm text-fieldiq-text">{children}</p>;
}

/**
 * A modal for the downhole screens. `z-[60]`, not `z-50`: the mobile BottomNav
 * is z-50 and later in the DOM, so a z-50 modal is drawn UNDER it on a phone
 * (ConfirmModal still has that bug; it is shared, so it is not changed here).
 * Escape and a backdrop click both cancel; the panel scrolls inside the
 * viewport rather than running off it.
 */
export function DhDialog({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card ${wide ? 'max-w-3xl' : 'max-w-lg'}`}
      >
        <header className="border-b border-fieldiq-border px-5 py-4">
          <h2 className="text-base font-extrabold tracking-tight text-white">{title}</h2>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-5 py-3">{footer}</footer>}
      </div>
    </div>
  );
}

/**
 * A counter that moves only when `open` turns true — the one moment a form
 * should start from its source. Key a form's reset on this, never on the data
 * it starts from: a refetch hands back new objects (a run, a runs list) for
 * the same record, and a reset keyed on them wiped a half-typed form whenever
 * the list was read again — after another tab's save, a window refocus, or
 * this screen's own invalidation.
 */
export function useOpenCount(open: boolean): number {
  const [count, setCount] = React.useState(0);
  const was = React.useRef(false);
  React.useEffect(() => {
    if (open && !was.current) setCount((c) => c + 1);
    was.current = open;
  }, [open]);
  return count;
}

/** A labelled form field. The label wraps the control, so a click on it focuses it. */
export function DhField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    // min-w-0: a field is usually a grid item, and a grid item's default
    // min-width (auto) let a wide control push the dialog past a phone's edge.
    <label className="block min-w-0">
      <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-fieldiq-muted">{hint}</span>}
    </label>
  );
}

/** Text inputs and selects on the downhole forms. */
export const DH_INPUT =
  'h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none disabled:opacity-60';

/** A date or datetime-local input: DH_INPUT plus the iOS width fix in downhole.css. */
export const DH_DATE_INPUT = `${DH_INPUT} dh-datetime`;

/** A secondary button on the body (not the band): card fill, border, theme ink. */
export const DH_SECONDARY =
  'inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm font-bold text-fieldiq-text transition hover:bg-fieldiq-dark/60 disabled:cursor-not-allowed disabled:opacity-50';

/** A secondary action on the band: white/25 edge and no colour class, so it keeps the band's ink in both themes. */
export const DH_BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50';
